<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="module-overlay">
        <div class="module-backdrop" @click.stop></div>

        <div
         class="module-container"
         @keyup.enter.stop.prevent="handleSave">

          <div class="module-content">
            <!-- Header -->
            <div class="mb-4">
              <h3 class="text-lg font-semibold text-center text-[var(--color-text)] leading-snug">
                {{ title }}
              </h3>
              <p v-if="subtitle" class="text-sm text-[var(--color-text)] opacity-70 text-center mt-1">
                {{ subtitle }}
              </p>
            </div>

            <!-- Content Slot - For custom form fields -->
            <div class="mb-4">
              <slot name="content" :formData="formData" :updateField="updateField" :submitForm="handleSave" />
            </div>

            <!-- Action Buttons -->
            <div v-if="!hideActions" class="flex justify-end gap-3">
              <button @click="handleCancel" class="btn btn--secondary">
                {{ cancelLabel || $t('common.buttons.cancel') }}
              </button>
              <button
                @click="handleSave"
                :disabled="!isValid"
                class="btn btn--primary"
                :class="{ 'opacity-50 cursor-not-allowed': !isValid }">
                {{ saveLabel || $t('common.buttons.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  inputLabel: {
    type: String,
    default: ''
  },
  saveLabel: {
    type: String,
    default: ''
  },
  cancelLabel: {
    type: String,
    default: ''
  },
  hideActions: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({})
  },
  validationFn: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['save', 'cancel', 'update:show'])

const formData = ref({})

// Watch for show prop changes to reset form
watch(() => props.show, (newValue) => {
  if (newValue) {
    formData.value = { ...props.initialData }
  }
})

// Validation
const isValid = computed(() => {
  if (props.validationFn) {
    return props.validationFn(formData.value)
  }
  // Default validation - check if any field has a value
  return Object.values(formData.value).some(val => val !== null && val !== '' && val !== undefined)
})

// Helper to update form fields from slot
function updateField(key, value) {
  formData.value[key] = value
}

function handleSave() {
  if (!isValid.value) return
  emit('save', formData.value)
  emit('update:show', false)
}

function handleCancel() {
  emit('cancel')
  emit('update:show', false)
}
</script>

<style scoped>
/* Transition classes */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
}

/* Overlay styles */
.module-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.module-backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(2px);
}

.module-container {
  position: relative;
  width: 100%;
  max-width: 28rem;
  z-index: 10;
}

.module-content {
  background-color: var(--color-primary-300);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Buttons */
.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s;
  cursor: pointer;
}
</style>